import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { fetchData } from "../../api/client";
import { downloadTextFile } from "./concessionCepExport";
import { formatCep8, unattendedToCsv } from "./cepUnattended";

function payloadRanges(cepRanges) {
  return (cepRanges || []).map((r, idx) => ({
    id: r.id,
    region_key: r.region_key,
    start_cep: r.start_cep,
    end_cep: r.end_cep,
    target_cod_interno: r.target_cod_interno,
    status: r.status,
    ordem: r.ordem != null ? r.ordem : idx,
  }));
}

function unwrapCoverage(data) {
  if (data?.unattended || data?.points || data?.summary) return data;
  if (data?.data && !Array.isArray(data.data)) return data.data;
  return data || {};
}

export default function CepUnattendedDialog({
  open,
  onClose,
  companyId,
  cepRanges = [],
  onViewCep,
}) {
  const [source, setSource] = useState("form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = async (src) => {
    setBusy(true);
    setError(null);
    try {
      let data;
      if (src === "prod") {
        if (!companyId) throw new Error("Salve a empresa para ver as lacunas gravadas.");
        data = await fetchData(`/companies/${companyId}/cep-coverage`, "GET", null, {
          timeout: 60000,
        });
      } else {
        data = await fetchData(
          "/companies/cep-coverage",
          "POST",
          { cepRanges: payloadRanges(cepRanges) },
          { timeout: 60000 }
        );
      }
      setReport(unwrapCoverage(data));
      setPage(0);
    } catch (err) {
      setReport(null);
      setError(err?.message || "Falha ao listar CEPs não atendidos.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    setSource("form");
    setQ("");
    setKindFilter("all");
    setPage(0);
    load("form");
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cities = report?.unattended || [];
  const summary = report?.summary || {};
  const qNorm = q.trim().toLowerCase();

  const filteredCities = useMemo(() => {
    return cities.filter((c) => {
      if (kindFilter === "none" && c.kind !== "none") return false;
      if (kindFilter === "partial" && c.kind !== "partial") return false;
      if (!qNorm) return true;
      const hay = `${c.city || ""} ${c.uf || ""} ${c.ibge || ""} ${(c.uncovered || [])
        .map((g) => `${g.start_cep}${g.end_cep}`)
        .join(" ")}`.toLowerCase();
      return hay.includes(qNorm);
    });
  }, [cities, qNorm, kindFilter]);

  const pageCities = filteredCities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const downloadCsv = () => {
    downloadTextFile("ceps-nao-atendidos.csv", unattendedToCsv(filteredCities));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        CEPs não atendidos
        <Typography variant="body2" color="text.secondary">
          Municípios sem nenhum CEP na cerca e cidades tocadas com trechos oficiais (IBGE) ainda sem
          atendimento.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {busy ? <LinearProgress sx={{ mb: 2 }} /> : null}
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Qual versão das faixas validar
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={source === "form" ? "contained" : "outlined"}
              onClick={() => {
                setSource("form");
                load("form");
              }}
            >
              Edição desta tela
            </Button>
            <Tooltip
              title={
                companyId
                  ? "Faixas já salvas no banco — é o que o e-commerce usa hoje."
                  : "Salve a empresa para comparar com a versão publicada."
              }
            >
              <span>
                <Button
                  size="small"
                  variant={source === "prod" ? "contained" : "outlined"}
                  disabled={!companyId}
                  onClick={() => {
                    setSource("prod");
                    load("prod");
                  }}
                >
                  Já salvo (site)
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!error && report ? (
            <Alert severity={summary.unattendedCities ? "warning" : "success"}>
              {summary.unattendedCities
                ? `${summary.unattendedNone || 0} cidade(s) sem cerca · ${
                    summary.unattendedPartial || 0
                  } com faixa incompleta · ${summary.unattendedRanges || 0} range(s).`
                : "Nenhuma cidade sem atendimento na base municipal."}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {[
              ["all", `Todas (${summary.unattendedCities || cities.length || 0})`],
              ["none", `Sem cerca (${summary.unattendedNone || 0})`],
              ["partial", `Incompleta (${summary.unattendedPartial || 0})`],
            ].map(([id, label]) => (
              <Button
                key={id}
                size="small"
                variant={kindFilter === id ? "contained" : "outlined"}
                onClick={() => {
                  setKindFilter(id);
                  setPage(0);
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              placeholder="Filtrar cidade, UF, IBGE ou CEP…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              fullWidth
            />
            <Button
              size="small"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadCsv}
              disabled={!filteredCities.length}
            >
              Baixar CSV
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cidade</TableCell>
                <TableCell>Faixa oficial</TableCell>
                <TableCell>Não atendido</TableCell>
                <TableCell align="right">Cobertura</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageCities.map((city) => (
                <TableRow key={`${city.ibge}-${city.start_cep}-${city.end_cep}`}>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography variant="body2">
                      {city.city || "—"}
                      {city.uf ? `/${city.uf}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      IBGE {city.ibge || "—"}
                    </Typography>
                    <Chip
                      size="small"
                      sx={{ mt: 0.5, height: 20 }}
                      color={city.kind === "partial" ? "warning" : "error"}
                      label={city.kind === "partial" ? "Faixa incompleta" : "Sem cerca"}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography variant="body2">
                      {formatCep8(city.start_cep)}–{formatCep8(city.end_cep)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    {(city.uncovered || []).map((gap) => (
                      <Typography key={`${gap.start_cep}-${gap.end_cep}`} variant="body2">
                        {onViewCep ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ minWidth: 0, px: 0, textTransform: "none" }}
                            onClick={() => onViewCep(gap.start_cep)}
                          >
                            {formatCep8(gap.start_cep)}–{formatCep8(gap.end_cep)}
                          </Button>
                        ) : (
                          `${formatCep8(gap.start_cep)}–${formatCep8(gap.end_cep)}`
                        )}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {` · ${gap.ceps?.toLocaleString("pt-BR") || 0} CEP(s)`}
                        </Typography>
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: "top" }}>
                    <Typography variant="body2">{city.coveragePct}%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(city.uncoveredCeps || 0).toLocaleString("pt-BR")} fora
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!pageCities.length ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      {busy
                        ? "Calculando cobertura municipal…"
                        : qNorm
                          ? "Nenhuma cidade neste filtro."
                          : "Nenhuma lacuna para listar."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredCities.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Por página"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
